/**
 * Provider grid for the agency Integrations tab.
 *
 * Art, 2026-07-21, wanted the platform to *look* like it connects to more than
 * Google Ads — partly as a selling signal when showing the tool to a client.
 *
 * Deliberately a STATIC grid, not a generic provider abstraction. Only Google
 * Ads has a working OAuth flow; generalizing `link`/`callback` to any provider
 * (#943) is real work and buys nothing until a second provider actually
 * exists. This renders the roadmap honestly and stops there.
 *
 * The honesty matters more than the grid. A card that looks clickable and does
 * nothing costs more trust than an absent card — and this codebase has been
 * bitten repeatedly by surfaces that look configured and aren't. So the
 * unbuilt providers are visibly, unmistakably not ready: muted, no button, and
 * labelled "Coming soon". Nobody should be able to click one and wonder
 * whether it silently failed.
 */
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';

interface ProviderSpec {
  key: string;
  name: string;
  /** What it does for us, in the operator's terms — not marketing copy. */
  description: string;
  /** Set when there is something real behind it, e.g. a developer key. */
  note?: string;
}

/**
 * Providers on the roadmap, in the order Art named them.
 *
 * Bing carries a note because we now hold the developer key, so it is the next
 * one that can genuinely start — worth distinguishing from the ones that are
 * only aspirations.
 */
const UPCOMING_PROVIDERS: ProviderSpec[] = [
  {
    key: 'google_analytics',
    name: 'Google Analytics',
    description: 'Site and conversion data alongside ad performance.',
  },
  {
    key: 'bing_ads',
    name: 'Bing Ads',
    description:
      'Microsoft Advertising search terms and negative keywords, where the work is still manual today.',
    note: 'Developer key in hand',
  },
  {
    key: 'facebook_ads',
    name: 'Facebook Ads',
    description: 'Meta campaign performance and creative-level reporting.',
  },
];

export function IntegrationProviderGrid() {
  return (
    <div>
      <div className="mb-3">
        {/* Explicit foreground: the heading is the one thing here that must
            stay fully legible. The CARDS are deliberately recessive; the
            section label introducing them is not. */}
        <h3 className="text-sm font-semibold text-foreground">
          More integrations
        </h3>
        <p className="text-xs text-muted-foreground">
          Not connectable yet. Listed so the roadmap is visible, not to imply
          they work.
        </p>
      </div>

      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
        {UPCOMING_PROVIDERS.map((provider) => (
          <Card
            key={provider.key}
            // Muted and non-interactive on purpose: no hover, no cursor
            // change, no button. Nothing here should read as clickable.
            className="border-dashed bg-muted/30 shadow-none"
            aria-disabled="true"
          >
            <CardHeader className="pb-2">
              <div className="flex items-start justify-between gap-2">
                <CardTitle className="text-sm font-medium text-muted-foreground">
                  {provider.name}
                </CardTitle>
                <Badge variant="outline" className="shrink-0 text-xs font-normal">
                  Coming soon
                </Badge>
              </div>
            </CardHeader>
            <CardContent className="pt-0">
              <p className="text-xs text-muted-foreground">
                {provider.description}
              </p>
              {provider.note ? (
                <p className="mt-2 text-xs font-medium text-muted-foreground">
                  {provider.note}
                </p>
              ) : null}
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  );
}
